import { StudentSectionSettingTypeCodes } from 'const';
import { useAppSelector, useDataLoad, useWidgetVisibility } from 'hooks';
import {
  independentDiagnosticAnalyticPeriodsSelector,
  independentDiagnosticRatingPeriodsSelector,
  selfDiagnosticSubjectsSelector,
} from 'selectors';
import { getDataLoadRequests, isDefined } from 'utils';

const sectionTypeCode = StudentSectionSettingTypeCodes.study;
const subsectionTypeCode = StudentSectionSettingTypeCodes.studyDiagnostic;

export const useDiagnosticsWidgetLoader = () => {
  // видимость раздела диагностик
  const mainWidgetVisibility = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
  });
  const { isSettingsMode, isWidgetVisible } = mainWidgetVisibility;

  // видимость подраздела независимые диагностики
  const independentSubsectionVisibility = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
    subcategoryTypeCode: StudentSectionSettingTypeCodes.diagnosticIndependent,
  });
  const { isWidgetVisible: isIndependentWidgetVisible } = independentSubsectionVisibility;

  const personalSubsectionVisibility = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
    subcategoryTypeCode: StudentSectionSettingTypeCodes.diagnosticPersonal,
  });
  const { isWidgetVisible: isPersonalWidgetVisible } = personalSubsectionVisibility;

  // видимость подраздела самодиагностики
  const selfSubsectionVisiblity = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
    subcategoryTypeCode: StudentSectionSettingTypeCodes.diagnosticSelf,
  });

  // видимость блока с рейтингом в независимых диагностиках
  const independentRatingVisibility = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
    subcategoryTypeCode: StudentSectionSettingTypeCodes.diagnosticIndependentRating,
  });
  const { isWidgetVisible: isIndependentRatingVisible } = independentRatingVisibility;

  // видимость блока со списком в независимых диагностиках
  const independentListVisibility = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
    subcategoryTypeCode: StudentSectionSettingTypeCodes.diagnosticIndependentList,
  });
  const { isWidgetVisible: isIndependentListVisible } = independentListVisibility;

  const isRatingBlockWisible = isWidgetVisible && isIndependentWidgetVisible && isIndependentRatingVisible;
  const isListBlockWisible = isWidgetVisible && isIndependentWidgetVisible && isIndependentListVisible;

  const requests = getDataLoadRequests();
  useDataLoad({ shouldLoad: isRatingBlockWisible, requests: requests.studyDiagnosticsOnlyRating });
  useDataLoad({ shouldLoad: isListBlockWisible, requests: requests.studyDiagnosticsOnlyList });
  useDataLoad({
    shouldLoad: isIndependentWidgetVisible && !isSettingsMode,
    requests: requests.studyDiagnosticsRatingAndList,
  });
  useDataLoad({
    shouldLoad: isIndependentWidgetVisible && isSettingsMode,
    requests: requests.studyDiagnosticsRatingAndListSettingsMode,
  });
  useDataLoad({
    shouldLoad: isPersonalWidgetVisible,
    requests: isSettingsMode
      ? requests.studyPersonalDiagnosticGroupedAll
      : requests.studyPersonalDiagnosticGroupedVisible,
  });

  const independentDiagnostics = useAppSelector((state) => state.independentDiagnostics);
  const selfDiagnostics = useAppSelector((state) => state.studentDiagnosis);
  const selfDiagnosticSubjects = useAppSelector(selfDiagnosticSubjectsSelector);
  const independentDiagnosticAnalyticPeriods = useAppSelector(independentDiagnosticAnalyticPeriodsSelector);
  const independentDiagnosticRatingPeriods = useAppSelector(independentDiagnosticRatingPeriodsSelector);
  const generalRating = useAppSelector((state) => state.independentDiagnosticsGeneralRating);
  const personalDiagnostics = useAppSelector((state) => state.personalDiagnostics);

  const visibility = {
    mainWidget: mainWidgetVisibility,
    independentSubsection: independentSubsectionVisibility,
    selfSubsection: selfSubsectionVisiblity,
    independentRating: independentRatingVisibility,
    independentList: independentListVisibility,
    personalDiagnostics: personalSubsectionVisibility,
  };

  const data = {
    independentDiagnostics,
    selfDiagnostics,
    selfDiagnosticSubjects,
    independentDiagnosticAnalyticPeriods,
    independentDiagnosticRatingPeriods,
    generalRating,
    personalDiagnostics,
  };

  const isLoading = [independentDiagnostics, generalRating, personalDiagnostics].some((v) => v?.loading);
  const hasData =
    independentDiagnostics.content.length > 0 ||
    isDefined(selfDiagnostics?.content?.totalResult) ||
    (generalRating.content ? generalRating.content.length > 0 : false) ||
    personalDiagnostics.content.length > 0;

  return {
    visibility,
    data,
    isLoading,
    hasData,
  };
};
